import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Button, Chip, Divider, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ScienceIcon from '@mui/icons-material/Science';
import DifferenceIcon from '@mui/icons-material/Difference';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DescriptionIcon from '@mui/icons-material/Description';

import { useProposal } from './state/ProposalContext.tsx';

type Seg = 'seeds' | 'gap' | 'roi' | 'draft';

export default function Sidebar() {
  const history = useHistory();
  const location = useLocation();

  const {
    sessionId,
    selectedSeedId,
    hasGapForCurrent,
    hasRoiForCurrent,
  } = useProposal();

  const base = location.pathname.replace(/\/(seeds|gap|roi|draft).*/, '');
  const isActive = (seg: Seg) => location.pathname.includes(`/${seg}`);

  const canSeeds = true;
  const canGap = Boolean(sessionId) && Boolean(selectedSeedId);
  const canRoi = Boolean(sessionId) && Boolean(selectedSeedId);
  const canDraft = Boolean(sessionId) && Boolean(selectedSeedId) && hasGapForCurrent && hasRoiForCurrent;

  const go = (seg: Seg, enabled: boolean) => {
    if (!enabled) {
      console.group('[Sidebar] 遷移ガード');
      console.log('遷移不可:', seg, { sessionId, selectedSeedId, hasGapForCurrent, hasRoiForCurrent });
      console.groupEnd();
    } else {
      history.push(`${base}/${seg}`);
    }
  };

  return (
    <Box
      sx={{
        width: 240,
        minWidth: 240,
        height: '100%',
        p: 1.5,
        bgcolor: '#e8f1fb',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          セッションID
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {sessionId ?? '—'}
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          選択中シーズ（seed_id）
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {selectedSeedId ?? '—'}
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          進捗
        </Typography>
        <Chip size="small" color={hasGapForCurrent ? 'success' : 'default'} label={hasGapForCurrent ? 'GAP: あり' : 'GAP: なし'} />
        <Chip size="small" color={hasRoiForCurrent ? 'success' : 'default'} label={hasRoiForCurrent ? 'ROI: あり' : 'ROI: なし'} />
      </Box>

      {/* ① シーズ */}
      <Button
        fullWidth
        startIcon={<ScienceIcon />}
        variant={isActive('seeds') ? 'contained' : 'outlined'}
        onClick={() => go('seeds', canSeeds)}
      >
        ①シーズ
      </Button>

      <ArrowDownwardIcon fontSize="small" sx={{ alignSelf: 'center', my: 0.5 }} />

      {/* ② GAP */}
      <Button
        fullWidth
        startIcon={<DifferenceIcon />}
        variant={isActive('gap') ? 'contained' : 'outlined'}
        disabled={!canGap}
        onClick={() => go('gap', canGap)}
      >
        ②GAP分析
      </Button>

      <ArrowDownwardIcon fontSize="small" sx={{ alignSelf: 'center', my: 0.5 }} />

      {/* ③ ROI */}
      <Button
        fullWidth
        startIcon={<ShowChartIcon />}
        variant={isActive('roi') ? 'contained' : 'outlined'}
        disabled={!canRoi}
        onClick={() => go('roi', canRoi)}
      >
        ③ROI試算
      </Button>

      <ArrowDownwardIcon fontSize="small" sx={{ alignSelf: 'center', my: 0.5 }} />

      {/* ④ Draft */}
      <Button
        fullWidth
        startIcon={<DescriptionIcon />}
        variant={isActive('draft') ? 'contained' : 'outlined'}
        disabled={!canDraft}
        onClick={() => go('draft', canDraft)}
      >
        ④ドラフト
      </Button>

      <Divider sx={{ my: 1 }} />

      <Button variant="outlined" onClick={() => history.push('/')}>
        一覧に戻る
      </Button>
    </Box>
  );
}